import { motion } from "framer-motion";
import { addDoc, collection, getDocs, query, where } from "firebase/firestore";
import { 
  AlertCircle, 
  ArrowLeft, 
  Copy, 
  Key, 
  Plus, 
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { auth, db } from "../../lib/firebase";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { Toast } from "../../components/Toast";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  createColumnHelper,
  flexRender,
} from '@tanstack/react-table';

interface AssessmentCode {
  id: string;
  code: string;
  partnerId: string;
  tier: string;
  used: boolean;
  createdAt: Date;
  usedBy?: string;
  usedByCoachName?: string;
  usedByCoachEmail?: string;
}

const columnHelper = createColumnHelper<AssessmentCode>();

export function PartnerAssessmentCodes() {
  const [codes, setCodes] = useState<AssessmentCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState("basic");
  const [globalFilter, setGlobalFilter] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [lastGeneratedCode, setLastGeneratedCode] = useState<string>("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const columns = useMemo(() => [
    columnHelper.accessor('code', {
      header: 'Assessment Code',
      cell: (info) => {
        const code = info.getValue();
        const isNew = code === lastGeneratedCode && showSuccessMessage;
        return (
          <div className="flex items-center gap-2">
            <span className="font-mono text-lg font-medium text-gray-900">
              {code}
            </span>
            {isNew && (
              <span className="bg-emerald-500 text-white text-xs px-2 py-1 rounded-full font-bold animate-pulse">
                NEW
              </span>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor('tier', {
      header: 'Tier',
      cell: (info) => (
        <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
          {info.getValue().replace('_', ' ')}
        </span>
      ),
    }),
    columnHelper.accessor('used', {
      header: 'Status',
      cell: (info) => (
        <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
          info.getValue() 
            ? 'bg-red-100 text-red-800' 
            : 'bg-emerald-100 text-emerald-800'
        }`}>
          {info.getValue() ? 'Used' : 'Available'}
        </span>
      ),
      filterFn: (row, columnId, value) => {
        if (value === 'all') return true;
        const isUsed = row.getValue(columnId);
        return (value === 'used' ? isUsed : !isUsed) as boolean;
      },
    }),
    columnHelper.accessor('usedByCoachName', {
      header: 'Used By Coach',
      cell: (info) => {
        const coachData = info.row.original;
        return (
          <div className="text-gray-600">
            {coachData.usedByCoachName ? (
              <div>
                <div className="font-medium text-gray-900">{coachData.usedByCoachName}</div>
                <div className="text-sm text-gray-500">({coachData.usedByCoachEmail})</div>
              </div>
            ) : (
              '-'
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor('createdAt', {
      header: 'Created Date',
      cell: (info) => {
        const date = info.getValue();
        return (
          <div className="text-gray-600">
            <div>{date.toLocaleDateString()}</div>
            <div className="text-sm text-gray-500">
              {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => {
        const code = info.row.original;
        return (
          <button
            onClick={() => copyToClipboard(code.code)}
            className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition-all"
            title="Copy Code"
          >
            <Copy className="w-4 h-4" />
          </button>
        );
      },
    }),
  ], [lastGeneratedCode, showSuccessMessage]);

  const filteredData = useMemo(() => {
    return codes.filter(code => {
      // Tier filter
      if (tierFilter !== "all" && code.tier !== tierFilter) {
        return false;
      }
      
      // Status filter
      if (statusFilter === "used" && !code.used) {
        return false;
      }
      if (statusFilter === "available" && code.used) {
        return false;
      }
      
      return true;
    });
  }, [codes, tierFilter, statusFilter]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: 'includesString',
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  useEffect(() => {
    if (auth?.currentUser?.uid) {
      loadCodes();
    }
  }, [auth?.currentUser?.uid]);


  const loadCodes = async () => {
    try {
      setIsLoading(true);
      const partnerId = auth.currentUser?.uid;
      const codesQuery = query(
        collection(db, "assessment_codes"),
        where("partnerId", "==", partnerId)
      );
      const codesSnapshot = await getDocs(codesQuery);

      const codesData = await Promise.all(
        codesSnapshot.docs.map(async (doc) => {
          const data = doc.data();
          let usedByCoachName = '';
          let usedByCoachEmail = '';
          
          // If code is used, try to fetch coach data using different possible fields
          if (data.used && (data.usedBy || data.coachId)) {
            try {
              // Try usedBy first, then fall back to coachId
              const coachUserId = data.usedBy || data.coachId;
              
              const userQuery = query(
                collection(db, "users"),
                where("userId", "==", coachUserId)
              );
              const userSnapshot = await getDocs(userQuery);
              
              if (!userSnapshot.empty) {
                const userData = userSnapshot.docs[0].data();
                usedByCoachName = userData.fullName || '';
                usedByCoachEmail = userData.email || '';
              } else {
                console.warn('No user found for coach ID:', coachUserId);
              }
            } catch (error) {
              console.warn('Could not fetch coach data for code:', doc.id, error);
            }
          }
          
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt.toDate(),
            usedByCoachName,
              usedByCoachEmail,
          } as AssessmentCode;
        })
      );
      
      // Sort by createdAt in descending order (newest first) in JavaScript
      codesData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      
      setCodes(codesData);
    } catch (err) {
      console.error("Error loading codes:", err);
      setError("Failed to load assessment codes");
    } finally {
      setIsLoading(false);
    }
  };

  const generateCode = () => {
    return "PBP" + Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const handleGenerateCode = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const partnerId = auth.currentUser?.uid;

      const newCode = generateCode();
      await addDoc(collection(db, "assessment_codes"), {
        code: newCode,
        partnerId,
        tier: selectedTier,
        used: false,
        createdAt: new Date(),
      });

      // Store the generated code and show success message
      setLastGeneratedCode(newCode);
      setShowSuccessMessage(true);
      
      setShowGenerateModal(false);
      loadCodes();
      
      // Hide success message after 10 seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 10000);
    } catch (err) {
      console.error("Error generating code:", err);
      setError("Failed to generate assessment code");
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setToastMessage(`Code ${code} copied successfully!`);
      setShowToast(true);
    } catch (error) {
      console.error('Failed to copy code:', error);
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setToastMessage(`Code ${code} copied successfully!`);
      setShowToast(true);
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading assessment codes..." />;
  }

  return (
    <div className="min-h-screen minimal-bg py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <Link
          to="/partner"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Key className="w-12 h-12 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Assessment Codes
              </h1>
              <p className="text-gray-600">
                Generate and manage assessment codes
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowGenerateModal(true)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r 
              from-blue-500 to-indigo-600 text-white font-medium hover:scale-105 transition-all"
          >
            <Plus className="w-5 h-5" />
            Generate Code
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 text-red-400 p-4 rounded-xl mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        {/* Success Message for Generated Code */}
        {showSuccessMessage && lastGeneratedCode && (
          <div className="bg-emerald-50 text-emerald-600 p-6 rounded-xl mb-6 border border-emerald-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold mb-2">✅ Assessment Code Generated Successfully!</h3>
                <p className="text-emerald-700">
                  New code: <span className="font-mono text-xl font-bold bg-emerald-100 px-3 py-1 rounded">{lastGeneratedCode}</span>
                </p>
                <p className="text-emerald-600 text-sm mt-2">This code appears at the top of the list below.</p>
              </div>
              <button
                onClick={() => setShowSuccessMessage(false)}
                className="text-emerald-600 hover:text-emerald-700 text-xl"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl p-6 mb-8 shadow-sm border border-gray-200">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search codes..."
                value={globalFilter ?? ''}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-gray-300 
                  focus:border-blue-500 focus:outline-none text-gray-900"
              />
            </div>
            
            <div>
              <select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 
                  focus:border-blue-500 focus:outline-none text-gray-900"
              >
                <option value="all">All Tiers</option>
                <option value="basic">Basic</option>
                <option value="basic_plus">Basic Plus</option>
                <option value="advanced">Advanced</option>
                <option value="partner">Partner</option>
              </select>
            </div>
            
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 
                  focus:border-blue-500 focus:outline-none text-gray-900"
              >
                <option value="all">All Status</option>
                <option value="available">Available</option>
                <option value="used">Used</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th 
                        key={header.id}
                        className="px-6 py-4 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-2">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          {{
                            asc: ' ↑',
                            desc: ' ↓',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-200">
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                      {codes.length === 0 ? "No assessment codes found" : "No codes match your search criteria"}
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map(row => {
                    const isNew = row.original.code === lastGeneratedCode && showSuccessMessage;
                    return (
                      <motion.tr
                        key={row.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`hover:bg-gray-50 transition-colors ${
                          isNew ? 'bg-emerald-50 border-l-4 border-emerald-500' : ''
                        }`}
                      >
                        {row.getVisibleCells().map(cell => (
                          <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>
                  Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
                  {Math.min(
                    (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                    table.getFilteredRowModel().rows.length
                  )}{' '}
                  of {table.getFilteredRowModel().rows.length} results
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                  className="p-2 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="p-2 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Page</span>
                  <input
                    type="number"
                    value={table.getState().pagination.pageIndex + 1}
                    onChange={(e) => {
                      const page = e.target.value ? Number(e.target.value) - 1 : 0;
                      table.setPageIndex(page);
                    }}
                    className="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm"
                    min="1"
                    max={table.getPageCount()}
                  />
                  <span className="text-sm text-gray-600">of {table.getPageCount()}</span>
                </div>
                
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="p-2 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                
                <button
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                  className="p-2 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
                
                <select
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => table.setPageSize(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  {[5, 10, 20, 50].map(pageSize => (
                    <option key={pageSize} value={pageSize}>
                      Show {pageSize}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Generate Code Modal */}
        {showGenerateModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-3xl p-8 w-full max-w-lg mx-4 shadow-xl"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Generate Assessment Code
              </h2>
              <form onSubmit={handleGenerateCode} className="space-y-6">
                <div>
                  <label htmlFor="tier" className="block text-gray-900 mb-2">
                    Coach Tier
                  </label>
                  <select
                    id="tier"
                    value={selectedTier}
                    onChange={(e) => setSelectedTier(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 
                      focus:border-blue-500 focus:outline-none text-gray-900"
                    required
                  >
                    <option value="basic">Basic</option>
                    <option value="basic_plus">Basic Plus</option>
                    <option value="advanced">Advanced</option>
                    <option value="partner">Partner</option>
                  </select>
                </div>

                <div className="flex justify-end gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowGenerateModal(false)}
                    className="px-6 py-3 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 
                      text-white font-medium hover:scale-105 transition-all disabled:opacity-50"
                  >
                    {isLoading ? "Generating..." : "Generate Code"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </div>
      
      <Toast
        isVisible={showToast}
        message={toastMessage}
        type="success"
        onClose={() => setShowToast(false)}
      />
    </div>
  );
}